// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react';
import { usePageTitle } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { useCookieSettings } from '@calendly/onetrust';
import { Button } from '@calendly/ui/components/button';
import { FlexLayout } from '@calendly/ui/components/flex-layout';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { TabContainer, TabContent, PageHeaderWithTabs } from 'components/page_elements';
import { generateTabs } from 'features/admin/helpers';
import { BookingVerificationSettings, BlockedSourcesSettings } from 'features/organization_settings';
import { parseSettingsTabFromSearch } from 'features/settings_modal/constants';
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context';
import { generateBrowserTabs, legacySecurityNestedBrowserRedirectTarget, PERSONAL_SECURITY_BASE } from '../personal_browser_urls';
import { getPersonalPrivacyTabs, getPersonalSecurityBrowserTabs } from '../settings_section_tabs';
const cookiesContentCss = "c1ikgv3d";
const cookiesTextCss = "c1g5darb";
const BookingTab = () => {
  const inSettingsModal = useIsSettingsModalLayout();
  return <TabContainer>
      {inSettingsModal && <TitleText level={2} size="small">
          {I18n.t('publisher.admin.security.tabs.booking')}
        </TitleText>}
      <TabContent>
        <BookingVerificationSettings />
      </TabContent>
    </TabContainer>;
};
const BlockedSourcesTab = () => {
  const inSettingsModal = useIsSettingsModalLayout();
  if (inSettingsModal) {
    return <TabContainer>
        <FlexLayout direction="column" gap={6}>
          <TitleText level={2} size="small">
            {I18n.t('publisher.personal_settings.privacy.tabs.blocked_people')}
          </TitleText>
          <BlockedSourcesSettings />
        </FlexLayout>
      </TabContainer>;
  }
  return <TabContainer>
      <TabContent>
        <BlockedSourcesSettings />
      </TabContent>
    </TabContainer>;
};
const CookiesTab = () => {
  const {
    toggleBanner
  } = useCookieSettings();
  return <TabContainer>
      <div className={cookiesContentCss}>
        <div className={cookiesTextCss}>
          <TitleText level={2} size="small">
            {I18n.t('publisher.personal_settings.privacy.cookie_preferences_title')}
          </TitleText>
          <BodyText color="muted">
            {I18n.t('publisher.personal_settings.privacy.cookies_description')}
          </BodyText>
        </div>
        <Button size="small" decoration="secondary-outline" onClick={toggleBanner}>
          {I18n.t('publisher.personal_settings.privacy.manage_cookies')}
        </Button>
      </div>
    </TabContainer>;
};
const resolveSecurityBrowserTab = (search, validTabSlugs, defaultTabSlug) => {
  const tabSlug = parseSettingsTabFromSearch(search);
  if (tabSlug != null && validTabSlugs.has(tabSlug)) {
    return tabSlug;
  }
  return defaultTabSlug;
};
export const SecurityPage = () => {
  const {
    pathname,
    search,
    hash
  } = useLocation();
  const inSettingsModal = useIsSettingsModalLayout();
  const settingsModalShellOwnsTabs = inSettingsModal;
  const reactiveBlockEnabled = useFlag('trust_and_safety_reactive_block');
  const bookingVerificationEnabled = useFlag('trust_and_safety_email_verification_feature');
  const privacyTabs = getPersonalPrivacyTabs({
    bookingVerificationEnabled,
    reactiveBlockEnabled
  });
  const browserTabs = getPersonalSecurityBrowserTabs({
    bookingVerificationEnabled,
    reactiveBlockEnabled
  });
  const browserTabSlugs = useMemo(() => new Set(browserTabs.map(tab => parseSettingsTabFromSearch(typeof tab.route === 'string' ? '' : tab.route.search ?? '')).filter(Boolean)), [browserTabs]);
  const defaultBrowserTabSlug = browserTabSlugs.values().next().value ?? null;
  const legacyBrowserRedirect = !inSettingsModal ? legacySecurityNestedBrowserRedirectTarget(pathname, search, hash, browserTabSlugs) : null;
  usePageTitle(`${I18n.t('publisher.account_settings.navigation.security')} - ${I18n.t('publisher.personal_settings.title')}`);
  if (legacyBrowserRedirect != null) {
    return <Redirect to={legacyBrowserRedirect} />;
  }
  const activeBrowserTab = resolveSecurityBrowserTab(search, browserTabSlugs, defaultBrowserTabSlug);
  const memoryRoutes = <>
      <Switch>
        {privacyTabs.length > 0 && <Redirect exact from={PERSONAL_SECURITY_BASE} to={privacyTabs[0].route} />}
        {bookingVerificationEnabled && <Route path="/app/personal/security/booking" component={BookingTab} />}
        {reactiveBlockEnabled && <Route path="/app/personal/security/blocked_sources" component={BlockedSourcesTab} />}
        {inSettingsModal && <Route path="/app/personal/security/cookies" component={CookiesTab} />}
      </Switch>
      {privacyTabs.length === 0 && inSettingsModal && <TabContainer>
          <BodyText color="muted">
            {I18n.t('publisher.admin.security.modal_no_sections')}
          </BodyText>
        </TabContainer>}
    </>;
  const browserRoutes = <>
      {activeBrowserTab === 'booking' && bookingVerificationEnabled && <BookingTab />}
      {activeBrowserTab === 'blocked_sources' && reactiveBlockEnabled && <BlockedSourcesTab />}
      {browserTabs.length === 0 && <TabContainer>
          <BodyText color="muted">
            {I18n.t('publisher.admin.security.modal_no_sections')}
          </BodyText>
        </TabContainer>}
    </>;
  const routes = inSettingsModal ? memoryRoutes : browserRoutes;
  const tabSelection = inSettingsModal ? generateTabs({
    pathname,
    tabs: privacyTabs
  }) : generateBrowserTabs({
    pathname,
    search,
    tabs: browserTabs
  });
  if (settingsModalShellOwnsTabs) {
    return routes;
  }
  return <>
      <PageHeaderWithTabs hideTitleChrome={inSettingsModal} eyebrow={I18n.t('publisher.personal_settings.navigation.account_details')} title={I18n.t('publisher.account_settings.navigation.security')} tabs={tabSelection} />
      {routes}
    </>;
};

require("./security_page.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./security_page.js");